/* eslint-disable @typescript-eslint/ban-ts-comment */
import type { BrowserOptions } from '@sentry/react';
import type { NextPageContext } from 'next';
import { getCurrentHub } from '@sentry/browser';
import type { Options } from '@sentry/types';
import clone from 'lodash/clone';
import forEach from 'lodash/forEach';
import merge from 'lodash/merge';

import type { CaptureExceptionSentry, ConfigScope, Instruction } from './sentry-base';
import { executeReport } from './sentry-base';

// Save the errors before sentry packages loaded
export let errorQueue: Array<{ error: Error; instructions: Instruction[] }> | undefined = [];

// Sentry functions
let initSentry: ((options: BrowserOptions) => void) | undefined = undefined;
// eslint-disable-next-line
let captureExceptionSentry: CaptureExceptionSentry = undefined;
let configureScope: ConfigScope = undefined;
/**
 * Execute report queue
 */
export function executeQueue(queue = errorQueue): void {
  if (queue) {
    forEach(queue, (item) => {
      executeReport({ configureScope, captureExceptionSentry }, item.error, undefined, item.instructions);
    });
  }
}

/**
 * Initialize Sentry
 */
export const init = async (release = process.env.SENTRY_RELEASE): Promise<void> => {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
  const environment = process.env.NEXT_PUBLIC_STAGE || 'dev';
  const [sentryTracing, sentryReact] = await Promise.all([import('@sentry/tracing'), import('@sentry/react')]);

  // Fill these functions
  initSentry = sentryReact.init;
  captureExceptionSentry = sentryReact.captureException;
  // @ts-ignore
  configureScope = sentryReact.configureScope;

  const opts: BrowserOptions = {
    dsn,
    environment,
    release,
    maxBreadcrumbs: 50,
    normalizeDepth: 10,
    attachStacktrace: true,
    autoSessionTracking: true,
    integrations: [new sentryTracing.Integrations.BrowserTracing()],
    tracesSampleRate: 0.2, // 20% sampling.
  };

  if (environment === 'dev') {
    opts.beforeSend = () => null;
    opts.debug = false;
    opts.sampleRate = 1;
  }

  initSentry(opts);
  executeQueue();
  errorQueue = undefined;
};

/**
 * Update sentry options after init
 * @reference https://github.com/getsentry/sentry-javascript/issues/2039#issuecomment-486674574
 */
export const updateSentry = (opts: Partial<Options>) => {
  // Mutate source object directly
  merge(getCurrentHub()?.getClient?.()?.getOptions?.(), opts);
};

/**
 * Explicitly send exception to Sentry.
 *
 * @param err
 * @param ctx
 */
export const captureException = (err: Error, ctx?: NextPageContext): string | void => {
  if (configureScope && captureExceptionSentry) {
    return executeReport({ configureScope, captureExceptionSentry }, err, ctx);
  } else if (errorQueue) {
    const instructions: Instruction[] = [];
    const error = err;
    if (err.message) {
      instructions.push(['setFingerprint', [clone(err.message)]]);
    }

    if (ctx) {
      const { query, pathname } = ctx;
      if (typeof window !== 'undefined') {
        instructions.push(['setTag', 'ssr', 'false']);
        instructions.push(['setExtra', 'query', clone(query)]);
        instructions.push(['setExtra', 'pathname', clone(pathname)]);
      }
    }
    errorQueue.push({ error, instructions });
  }
};
